import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Input,
  Textarea,
  Button,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Checkbox,
  Card,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
  InfoTooltip,
} from '@artstorefronts/ui';
import { Wand2, Loader2 } from 'lucide-react';
import { IMAGE_ID_TOKEN, isLinkOutUrlTemplate } from '@arthelper/shared/link-utils';
import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ContentWriter from './ContentWriter';
import PromptDataEditor from './PromptDataEditor';
import { useMutation } from '@apollo/client';
import { TEST_COMPLETION } from '../graphql/mutations';
import { Icon } from './Icon';
import { validateHtml, getAllowedHtmlHelp } from '../utils/htmlValidator';
import { DEFAULT_MODEL_ID } from '~/components/Chat/constants';

const AVAILABLE_PLANS = ['free', 'growth', 'pro'];

const promptDataSchema = z.array(
  z.object({
    role: z.enum(['system', 'user', 'assistant']),
    content: z.union([
      z.string(),
      z.array(
        z.object({
          type: z.string(),
          text: z.string().optional(),
          image_url: z
            .object({
              url: z.string(),
            })
            .optional(),
        })
      ),
    ]),
  })
);

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  icon: z.string().min(1, 'Icon is required'),
  additionalInfo: z.string().optional(),
  order: z.number().int().nonnegative(),
  promptData: promptDataSchema,
  permissionKey: z.string().min(1, 'Permission key is required'),
  planNames: z.string().min(1, 'Plan names are required'),
  expiresAt: z.string().optional(),
  selectedPlans: z.record(z.boolean()).optional(),
  model: z.string().min(1, 'Model is required'),
  signupHeaderText: z.string().optional(),
  signupFormLabelText: z.string().optional(),
  signupButtonText: z.string().optional(),
  linkOutUrl: z
    .string()
    .optional()
    .refine(
      (value) => !value?.trim() || isLinkOutUrlTemplate(value.trim()),
      `Enter a full http(s) URL. ${IMAGE_ID_TOKEN} is the only variable supported.`
    ),
  isLinkOutNewWindow: z.boolean().optional(),
});

// The API refuses a spark that configures neither a prompt nor a link-out URL, so
// the form says so on the field instead of surfacing a raw validation error from
// the mutation.
const sparkFormSchema = formSchema.superRefine((data, ctx) => {
  if (data.linkOutUrl?.trim() || data.promptData.length > 0) {
    return;
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['promptData'],
    message: 'Add prompt data or a link-out URL',
  });
});

function formatDateForInput(dateString: string | undefined) {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
}

function InferenceTable({ inferences }: { inferences: any[] }) {
  const { t } = useTranslation('billing');
  if (!inferences.length) return null;

  return (
    <div className="mt-4">
      <h3 className="text-sm font-semibold mb-2">{t('spark-form.inferences')}</h3>
      <div className="border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">{t('spark-form.tool')}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">{t('spark-form.arguments')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {inferences.map((inference, index) => (
              <tr key={index}>
                <td className="px-4 py-2 align-top">{inference.toolName}</td>
                <td className="px-4 py-2 whitespace-pre-wrap font-mono text-xs">
                  {JSON.stringify(inference.args.value, null, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SparkForm({ id, type, models, onSubmit }: any) {
  const { t } = useTranslation('billing');
  const { t: tc } = useTranslation('common');
  const [suggestion, setSuggestion] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedPlans, setSelectedPlans] = useState<string[]>(
    type?.planNames ? type.planNames.split(',').map((p: string) => p.trim()) : AVAILABLE_PLANS
  );
  const [sparkResponse, setSparkResponse] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string>('');
  const [inferences, setInferences] = useState<any[]>([]);
  const [htmlValidation, setHtmlValidation] = useState<{
    errors: string[];
    warnings: string[];
    isValid: boolean;
  }>({ errors: [], warnings: [], isValid: true });

  const [testCompletion] = useMutation(TEST_COMPLETION);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(sparkFormSchema),
    defaultValues: {
      name: type?.name || '',
      description: type?.description || '',
      icon: type?.icon || 'wand-sparkles',
      additionalInfo: type?.additionalInfo || '',
      order: type?.order || 0,
      promptData: typeof type?.promptData === 'string' ? JSON.parse(type.promptData) : type?.promptData || [],
      permissionKey: type?.permissionKey || 'deprecated',
      expiresAt: formatDateForInput(type?.expiresAt),
      planNames: type?.planNames || AVAILABLE_PLANS.join(','),
      model: type?.model || DEFAULT_MODEL_ID,
      signupHeaderText: type?.signupHeaderText || '',
      signupFormLabelText: type?.signupFormLabelText || '',
      signupButtonText: type?.signupButtonText || '',
      linkOutUrl: type?.linkOutUrl || '',
      isLinkOutNewWindow: type?.isLinkOutNewWindow ?? true,
    },
  });

  const currentIcon = watch('icon');
  // A spark either runs a prompt or links out, never both — the API rejects a
  // CompletionType carrying both, so the prompt fields go away once a URL is set.
  const isLinkOut = !!watch('linkOutUrl')?.trim();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'promptData',
  });

  const handleFormSubmit = (data: any) => {
    console.log({ data });

    // Prevent submission if there are HTML validation errors
    if (htmlValidation.errors.length > 0) {
      console.error('Cannot submit form with HTML validation errors:', htmlValidation.errors);
      return;
    }

    if (data.expiresAt === '') {
      data.expiresAt = null;
    }

    if (data.expiresAt) {
      data.expiresAt = new Date(data.expiresAt).toISOString();
    }

    const linkOutUrl = data.linkOutUrl?.trim() || null;

    onSubmit({
      ...data,
      linkOutUrl,
      // The API reads an empty promptData array as absent, but a link-out spark
      // still has to send an unambiguous null: the form always defaults
      // promptData to [], and a converted prompt spark would otherwise submit
      // its old messages alongside the new URL.
      promptData: linkOutUrl ? null : data.promptData,
      planNames: selectedPlans.join(','),
    });
  };

  function PlanCheckboxes() {
    return (
      <div className="mb-4">
        <Label className="font-bold mb-2 block">{t('spark-form.plan-names')}</Label>
        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.plan-names-description')}</div>
        <div className="flex space-x-3 mt-4">
          {AVAILABLE_PLANS.map((plan) => (
            <div key={plan} className="flex items-center space-x-2">
              <Checkbox
                id={`plan-${plan}`}
                checked={selectedPlans.includes(plan)}
                onCheckedChange={(checked) => {
                  const newSelectedPlans = checked ? [...selectedPlans, plan] : selectedPlans.filter((p) => p !== plan);
                  setSelectedPlans(newSelectedPlans);
                  setValue('planNames', newSelectedPlans.join(','));
                }}
              />
              <Label htmlFor={`plan-${plan}`} className="capitalize">
                {plan}
              </Label>
            </div>
          ))}
        </div>
        {errors.planNames && <p className="text-red-500 text-sm mt-1">{`${errors.planNames.message}`}</p>}
      </div>
    );
  }

  const getErrorCountByTab = useCallback(() => {
    const settingsErrors = Object.keys(errors).filter((key) =>
      [
        'name',
        'description',
        'icon',
        'order',
        'permissionKey',
        'planNames',
        'expiresAt',
        'signupHeaderText',
        'signupFormLabelText',
        'signupButtonText',
        'linkOutUrl',
        'isLinkOutNewWindow',
      ].includes(key)
    ).length;

    const promptErrors = Object.keys(errors).filter((key) => key === 'promptData').length;

    // Add HTML validation errors to settings tab
    const htmlErrors = htmlValidation.errors.length;

    return {
      settings: settingsErrors + htmlErrors,
      prompt: promptErrors,
    };
  }, [errors, htmlValidation.errors]);

  const errorCounts = getErrorCountByTab();

  // Handle HTML validation for additionalInfo
  const validateAdditionalInfo = useCallback((html: string) => {
    if (!html) {
      setHtmlValidation({ errors: [], warnings: [], isValid: true });
      return;
    }

    const validation = validateHtml(html);
    setHtmlValidation({
      errors: validation.errors,
      warnings: validation.warnings,
      isValid: validation.isValid,
    });
  }, []);

  function mapToVercel(content: any[]) {
    return content.map((item) => {
      if (item.type === 'text') {
        return { type: 'text', text: item.text };
      }
      if (item.type === 'image_url' || item.type === 'image') {
        return {
          type: 'image',
          image: item.image_url.url,
        };
      }
      return item;
    });
  }

  const handleGenerate = async (suggestion?: string) => {
    const promptData = getValues('promptData');
    const model = getValues('model');

    try {
      setIsGenerating(true);
      setGenerateError('');
      setSuggestion('');

      const messages = promptData;

      const { data } = await testCompletion({
        variables: {
          input: {
            model,
            completionTypeId: type?.id,
            messages: messages.map((msg) => ({
              role: msg.role,
              content: Array.isArray(msg.content) ? mapToVercel(msg.content) : msg.content,
            })),
            suggestion,
          },
        },
      });

      if (data?.testCompletion?.responseHtml) {
        setSparkResponse(data.testCompletion.responseHtml);
      }

      if (data?.testCompletion?.inference) {
        setInferences((prev) => [...prev, ...data.testCompletion.inference]);
      }
    } catch (error: any) {
      setGenerateError(error.message || t('spark-form.generation-error'));
      console.error('Generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <form id={id} ref={formRef} onSubmit={handleSubmit(handleFormSubmit)}>
        <Tabs defaultValue="settings" className="w-full">
          <div className="flex items-center justify-between mb-8">
            <TabsList>
              <TabsTrigger value="settings" className="relative">
                {t('spark-form.settings')}
                {errorCounts.settings > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center z-50">
                    {errorCounts.settings}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="prompt" className="relative" disabled={isLinkOut}>
                {t('spark-form.prompt-data')}
                {errorCounts.prompt > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
                    {errorCounts.prompt}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="grid gap-6 grid-cols-12">
            <div className="col-span-6">
              <Card>
                <CardContent className="pt-6">
                  <TabsContent value="settings">
                    <div className="grid gap-6 max-w-4xl">
                      <div className="mb-2">
                        <Label htmlFor="name" className="font-bold">
                          {tc('name')}
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.name-description')}</div>
                        <Input {...register('name')} id="name" />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{`${errors.name.message}`}</p>}
                      </div>

                      <div className="mb-2">
                        <Label htmlFor="description" className="font-bold">
                          {t('spark-form.description')}
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.description-help')}</div>
                        <Textarea {...register('description')} id="description" />
                        {errors.description && (
                          <p className="text-red-500 text-sm mt-1">{`${errors.description.message}`}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="icon" className="font-bold">
                            {t('spark-form.icon')}
                          </Label>
                          <div className="text-xs text-zinc-500 mb-2">{t('spark-form.icon-description')}</div>
                          <div className="relative">
                            <Input {...register('icon')} id="icon" />
                            {currentIcon && (
                              <div className="absolute top-2 right-3 z-50">
                                <Icon icon={currentIcon?.trim() || 'wand-sparkles'} size={6} />
                              </div>
                            )}
                          </div>
                          {errors.icon && <p className="text-red-500 text-sm mt-1">{`${errors.icon.message}`}</p>}
                        </div>
                        <div>
                          <Label htmlFor="order" className="font-bold">
                            {t('spark-form.order')}
                          </Label>
                          <div className="text-xs text-zinc-500 mb-2">{t('spark-form.order-description')}</div>
                          <Input type="number" {...register('order', { valueAsNumber: true })} id="order" />
                          {errors.order && <p className="text-red-500 text-sm mt-1">{`${errors.order.message}`}</p>}
                        </div>
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="icon" className="font-bold">
                          {t('spark-form.additional-info')}
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.additional-info-description')}</div>
                        <div className="flex">
                          <div className="flex-1">
                            <Textarea
                              {...register('additionalInfo')}
                              id="additionalInfo"
                              maxLength={1500}
                              onChange={(e) => {
                                register('additionalInfo').onChange(e);
                                validateAdditionalInfo(e.target.value);
                              }}
                            />

                            {/* HTML Validation Errors */}
                            {htmlValidation.errors.length > 0 && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                                <div className="font-medium text-red-800 mb-1">{t('spark-form.html-issues-found')}</div>
                                {htmlValidation.errors.map((error, index) => (
                                  <div key={index} className="text-red-700">
                                    • {error}
                                  </div>
                                ))}
                                <div className="mt-2 text-red-800 font-medium">
                                  {t('spark-form.html-issues-warning')}
                                </div>
                              </div>
                            )}

                            {/* HTML Validation Warnings */}
                            {htmlValidation.warnings.length > 0 && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                <div className="font-medium text-yellow-800 mb-1">{t('spark-form.warnings')}</div>
                                {htmlValidation.warnings.map((warning, index) => (
                                  <div key={index} className="text-yellow-700">
                                    • {warning}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* HTML Help */}
                            <details className="mt-2">
                              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-700">
                                {t('spark-form.show-allowed-html')}
                              </summary>
                              <div className="mt-1 p-2 bg-zinc-50 border border-zinc-200 rounded text-xs text-zinc-600 whitespace-pre-line">
                                {getAllowedHtmlHelp()}
                              </div>
                            </details>
                          </div>
                          <div className="w-10 flex justify-end">
                            {watch('additionalInfo') && (
                              <InfoTooltip title={watch('name') || ''} text={watch('additionalInfo') || ''} />
                            )}
                          </div>
                        </div>
                        {errors.additionalInfo && (
                          <p className="text-red-500 text-sm mt-1">{`${errors.additionalInfo.message}`}</p>
                        )}
                      </div>

                      <div className="mb-4">
                        <Label className="font-bold mb-2 block">{t('spark-form.signup-page-content')}</Label>
                        <div className="text-xs text-zinc-500 mb-4">{t('spark-form.signup-page-description')}</div>

                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="signupHeaderText" className="font-medium">
                              {t('spark-form.header-text')}
                            </Label>
                            <div className="text-xs text-zinc-500 mb-2">{t('spark-form.header-text-description')}</div>
                            <Input
                              {...register('signupHeaderText')}
                              id="signupHeaderText"
                              placeholder={t('spark-form.header-text-placeholder')}
                            />
                            {errors.signupHeaderText && (
                              <p className="text-red-500 text-sm mt-1">{`${errors.signupHeaderText.message}`}</p>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="signupFormLabelText" className="font-medium">
                              {t('spark-form.signup-form-label')}
                            </Label>
                            <div className="text-xs text-zinc-500 mb-2">
                              {t('spark-form.signup-form-label-description')}
                            </div>
                            <Input
                              {...register('signupFormLabelText')}
                              id="signupFormLabelText"
                              placeholder={t('spark-form.signup-form-placeholder')}
                            />
                            {errors.signupFormLabelText && (
                              <p className="text-red-500 text-sm mt-1">{`${errors.signupFormLabelText.message}`}</p>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="signupButtonText" className="font-medium">
                              {t('spark-form.button-text')}
                            </Label>
                            <div className="text-xs text-zinc-500 mb-2">{t('spark-form.button-text-description')}</div>
                            <Input
                              {...register('signupButtonText')}
                              id="signupButtonText"
                              placeholder={t('spark-form.button-text-placeholder')}
                            />
                            {errors.signupButtonText && (
                              <p className="text-red-500 text-sm mt-1">{`${errors.signupButtonText.message}`}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <Label className="font-bold mb-2 block">{t('spark-form.link-out')}</Label>
                        <div className="text-xs text-zinc-500 mb-4">{t('spark-form.link-out-description')}</div>

                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="linkOutUrl" className="font-medium">
                              {t('spark-form.link-out-url')}
                            </Label>
                            <div className="text-xs text-zinc-500 mb-2">
                              {t('spark-form.link-out-url-description', { token: IMAGE_ID_TOKEN })}
                            </div>
                            <Input
                              {...register('linkOutUrl')}
                              id="linkOutUrl"
                              placeholder={t('spark-form.link-out-url-placeholder', { token: IMAGE_ID_TOKEN })}
                            />
                            {errors.linkOutUrl && (
                              <p className="text-red-500 text-sm mt-1">{`${errors.linkOutUrl.message}`}</p>
                            )}
                          </div>

                          {isLinkOut && fields.length > 0 && (
                            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                              {t('spark-form.link-out-replaces-prompt')}
                            </div>
                          )}

                          <div className="flex items-start gap-2">
                            <Controller
                              name="isLinkOutNewWindow"
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  id="isLinkOutNewWindow"
                                  checked={field.value}
                                  onCheckedChange={(checked) => field.onChange(checked === true)}
                                />
                              )}
                            />
                            <div>
                              <Label htmlFor="isLinkOutNewWindow" className="font-medium">
                                {t('spark-form.link-out-open-in-new-window')}
                              </Label>
                              <div className="text-xs text-zinc-500">
                                {t('spark-form.link-out-open-in-new-window-description')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <Label htmlFor="permissionKey" className="font-bold">
                          {t('spark-form.permission-key')}
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.permission-key-description')}</div>
                        <Input {...register('permissionKey')} id="permissionKey" />
                        {errors.permissionKey && (
                          <p className="text-red-500 text-sm mt-1">{`${errors.permissionKey.message}`}</p>
                        )}
                      </div>

                      <PlanCheckboxes />

                      <div className="mb-4">
                        <Label htmlFor="expiresAt" className="font-bold">
                          {t('spark-form.expiration-date')}
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">{t('spark-form.expiration-date-description')}</div>
                        <Input type="date" {...register('expiresAt')} id="expiresAt" />
                        {errors.expiresAt && (
                          <p className="text-red-500 text-sm mt-1">{`${errors.expiresAt.message}`}</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="prompt">
                    {isLinkOut ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                        {t('spark-form.link-out-prompt-disabled')}
                      </div>
                    ) : (
                      <>
                        <div className="mb-6">
                          <Label htmlFor="model" className="font-bold">
                            {t('spark-form.model')}
                          </Label>
                          <div className="text-xs text-zinc-500 mb-2">{t('spark-form.model-description')}</div>
                          <Controller
                            name="model"
                            control={control}
                            render={({ field }) => (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger id="model" className="w-full">
                                  <SelectValue placeholder={t('spark-form.select-model')} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {models?.map((model: any) => (
                                      <SelectItem key={model.id} value={model.id}>
                                        <span className="text-xs text-zinc-500">{model.provider}</span> - {model.name}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.model && <p className="text-red-500 text-sm mt-1">{`${errors.model.message}`}</p>}
                        </div>
                        <PromptDataEditor
                          fields={fields}
                          append={append}
                          remove={remove}
                          register={register}
                          update={update}
                          getValues={getValues}
                        />
                        {errors.promptData?.message && (
                          <p className="text-red-500 text-sm mt-2">{`${errors.promptData.message}`}</p>
                        )}
                      </>
                    )}
                  </TabsContent>
                </CardContent>
              </Card>
            </div>

            <div className="col-span-6">
              <div className="flex flex-col gap-4">
                {generateError && (
                  <div className="bg-red-50 border border-red-500 text-red-500 py-2 px-4 mx-8 rounded-md text-sm">
                    {generateError}
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="mr-8"
                    onClick={() => handleGenerate(suggestion)}
                    disabled={isGenerating || isLinkOut}
                  >
                    {isGenerating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="mr-2 h-4 w-4" />
                    )}
                    {t('spark-form.generate')}
                  </Button>
                </div>
                <ContentWriter
                  hideControls={false}
                  completionType={type}
                  sparkResponse={sparkResponse}
                  isTyping={isGenerating}
                  completion={null}
                  loading={isGenerating}
                  suggestion={suggestion}
                  history={[]}
                  editedContent={null}
                  setEditedContent={() => {}}
                  setHistory={() => {}}
                  setSuggestion={setSuggestion}
                  onRestart={() => setSparkResponse('')}
                  onSubmitSuggestion={() => handleGenerate(suggestion)}
                />
                <InferenceTable inferences={inferences} />
              </div>
            </div>
          </div>
        </Tabs>
      </form>
    </div>
  );
}

export { SparkForm, formSchema };
export default SparkForm;
